import type { RetailMicroTransferDescriptor } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { BufferUtil } from "@solomon-labs/buffer";
import { MEMO_PROGRAM_ID_STRING } from "@solomon-labs/constants";
import { PublicKey } from "@solomon-labs/publickey";
import { type IsEncodable, SystemTransferInstruction } from "@solomon-labs/solana-codec";
import { Transaction } from "@solomon-labs/solana-transactions";
import type { SolanaAddress, SolanaBlockhash } from "@solomon-labs/types";

export const VERIFICATION_TRANSFER_LAMPORTS = 1000n;
export const VERIFICATION_TRANSFER_SOL = new BigDecimal(VERIFICATION_TRANSFER_LAMPORTS)
    .divide(BigDecimal.pow10(9))
    .toString();

export interface VerificationTransfer {
    readonly recipient: PublicKey;
    readonly lamports: bigint;
    readonly memo: string;
}

/** JSON is a wire format only: amounts become bigint before entering the transfer flow. */
export function normalizeVerificationTransfer(
    descriptor: RetailMicroTransferDescriptor | undefined,
    nonce: string | undefined
): VerificationTransfer | null {
    if (
        !descriptor ||
        !nonce ||
        typeof descriptor.recipient !== "string" ||
        !PublicKey.isValidBase58(descriptor.recipient) ||
        // Supported API versions differ only in JSON representation; neither path does number arithmetic.
        (typeof descriptor.lamports !== "string" && typeof descriptor.lamports !== "number") ||
        descriptor.lamports.toString() !== VERIFICATION_TRANSFER_LAMPORTS.toString() ||
        descriptor.memo !== `solomon-yaas:v1:${nonce}`
    )
        return null;
    return {
        recipient: new PublicKey(descriptor.recipient as SolanaAddress),
        lamports: VERIFICATION_TRANSFER_LAMPORTS,
        memo: descriptor.memo
    };
}

interface BuildVerificationTransferArgs {
    payer: PublicKey;
    blockhash: SolanaBlockhash;
    transfer: VerificationTransfer;
}

/** Exactly one System transfer and one signer-bound memo, serialized by the Solomon SDK. */
export function buildVerificationTransfer({ payer, blockhash, transfer }: BuildVerificationTransferArgs): Transaction {
    const memo = {
        programId: new PublicKey(MEMO_PROGRAM_ID_STRING),
        accounts: [{ pubkey: payer, isSigner: true, isWritable: false }],
        data: BufferUtil.fromString(transfer.memo, "utf8")
    };
    // The SDK's encodable path preserves binary data; plain instructions currently drop raw bytes.
    const memoInstruction: IsEncodable = {
        programId: memo.programId,
        encode: () => memo.data,
        accounts: () => memo.accounts,
        instruction: () => memo
    };
    return new Transaction()
        .setFeePayer(payer)
        .setRecentBlockhash(blockhash)
        .add(new SystemTransferInstruction({ from: payer, to: transfer.recipient, lamports: transfer.lamports }))
        .add(memoInstruction);
}
